if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const isBundleForm = this.hasAttribute('data-nutrivie-bundle-form');
        if (isBundleForm) this.syncBundleVariantId();

        const formData = new FormData(this.form);
        if (isBundleForm) {
          // The radio cards are the only bundle picker on this product. A bundle tier is one variant at quantity 1.
          // The Kaching Bundles app embed rewrites hidden quantity and deal fields and merges its own registered
          // bundle line into any /cart/add it can see, which stacked a stale tier beside the chosen one.
          formData.set('quantity', '1');
          formData.delete('properties[__kaching_bundles]');
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // kaching_bundles=true tells the Kaching Bundles interceptor that this add is already complete and must
        // not be merged with its registered bundle items.
        const addUrl = isBundleForm ? `${routes.cart_add_url}?kaching_bundles=true` : `${routes.cart_add_url}`;

        this.clearBundleLines()
          .then(() => fetch(addUrl, config))
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const renderCartContents = () => {
              let renderResult;
              CartPerformance.measure("add:paint-updated-sections", () => {
                renderResult = this.cart.renderContents(response);
              });

              if (renderResult && typeof renderResult.then === 'function') {
                return renderResult.then((didRender) => {
                  if (didRender === false) window.location.assign(window.routes?.cart_url || '/cart');
                  return didRender;
                });
              }

              if (renderResult === false) window.location.assign(window.routes?.cart_url || '/cart');
              return renderResult;
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    renderCartContents();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              const renderResult = renderCartContents();
              if (renderResult && typeof renderResult.then === 'function') await renderResult;
            }
          })
          .catch((e) => {
            console.error(e);
            // The add never ran when the bundle clear failed, so the cart still holds the previous tier.
            if (e && e.isBundleCartError) this.handleErrorMessage(window.cartStrings?.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Bundle tiers are separate variants of one product. Before a tier is added, every bundle line already
      // in the cart is cleared, including the same tier, so the add that follows leaves exactly one line at
      // quantity 1. The promise settles only after Shopify confirms the update, and rejects (so /cart/add.js
      // never runs) when the cart cannot be cleared.
      clearBundleLines() {
        if (!this.hasAttribute('data-nutrivie-bundle-form')) return Promise.resolve();

        const bundleVariantIds = this.bundleVariantIds();
        if (bundleVariantIds.length === 0) return Promise.resolve();

        const cartUrl = window.routes?.cart_url || '/cart';
        const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update';

        return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart read failed with status ${response.status}`);
            return response.json();
          })
          .then((cart) => {
            const updates = {};
            (cart.items || []).forEach((item) => {
              const variantId = String(item.variant_id);
              if (bundleVariantIds.includes(variantId)) updates[variantId] = 0;
            });
            if (Object.keys(updates).length === 0) return null;

            return fetch(`${cartUpdateUrl}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            }).then((response) => {
              if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
              return response.json();
            });
          })
          .catch((error) => {
            error.isBundleCartError = true;
            throw error;
          });
      }

      // Variant ids of every bundle tier rendered for this product. Read from the whole product block rather
      // than from radios tied to this form, because the theme's view transition detaches the previous radios
      // from the form while it swaps them out.
      bundleVariantIds() {
        const scope = this.closest('product-info') || document;
        return Array.from(scope.querySelectorAll('input[type="radio"][data-bundle-variant-id]'), (option) =>
          String(option.dataset.bundleVariantId)
        );
      }

      // Shopify reads the hidden id input. Make it match the checked bundle card at submit time, so the form
      // always posts the tier the merchant sees selected.
      syncBundleVariantId() {
        if (!this.hasAttribute('data-nutrivie-bundle-form') || !this.variantIdInput) return;

        const scope = this.closest('product-info') || document;
        const checked = Array.from(
          scope.querySelectorAll('input[type="radio"][data-bundle-variant-id]:checked')
        ).find((option) => option.form === this.form);
        if (checked) this.variantIdInput.value = checked.dataset.bundleVariantId;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
